"use client";

import { getServerCourseList } from "@/app/actions/AiCourseFinderActions";
import Back from "@/components/common/Back";
import PerfectMatch from "@/components/tools/checkEligibility/PerfectMatches";
import CustomPagination from "@/components/common/CustomPagination";
import Footer from "@/components/common/Footer";
import { useAuth } from "@/hook/useAuth";
import { useFullPath } from "@/hook/useFullPath";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { FilteredDataType, sortDataType } from "@/store/types/aiCourseFinder";
import { CourseItem, Courses } from "@/store/types/courseTypes";
import { University } from "@/store/types/universityTypes";
import {
  checkCountry,
  CoursesCardsRemark,
  replacedName,
} from "@/utils/Constants";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import { getImageBanner } from "@/utils/imageExtractor";
import { GitCompareArrows } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import EmailVerification from "../common/EmailVerificationPopup";
import CommonAuth from "../auth/CommonAuth";
import { Close, Filter, SortUpArrow } from "../common/Icons";
import { Dropdown } from "../common/Dropdown";
import { sortOptionsAllCourse } from "../common/Helper";
import { SkeletonCard } from "../common/SkeltonCard";
import TryAiCourseFindBanner from "../dashboard/TryAiCourseFindBanner";
import CoursesCards from "../common/CoursesCards";
import FilterSideBar from "../common/ai-course-finder/FilterSideBar";
import { setCourseComparisonData } from "@/store/feature/ai-course-finder/aiCourseFinderSlice";
import { NewNavBar } from "../common/NewNavBar";
import NewCoursesCards from "../newSearch/NewCoursesCards";
import CourseCard from "../tools/course-finder/CourseCard";
import UniversityCourseCard from "./UniversityCourseCard";

const AllCourseHome = ({
  universityDetail,
}: {
  universityDetail: University;
}) => {
  const params = useFullPath();
  const tab = useSearchParams();
  const paramsTab = tab.get("tab");
  const router = useRouter();
  const { isAnonymous } = useAuth();
  const dispatch = useAppDispatch();

  // ─── Course List State ───────────────────────────────────────────────────────
  const courseComparisonData = useAppSelector(
    (state) => state.aiCourseFinder.courseComparisonData || [],
  );
  const [selectedSort, setSelectedSort] = useState<string>("");
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  // ─── AllCourse State ─────────────────────────────────────────────────────────
  const [sortData, setSortData] = useState<sortDataType>({
    sortingKey: "",
    sortingDirection: "",
  });
  const [loading, setLoading] = useState(false);

  const [filteredData, setFilteredData] = useState<FilteredDataType>({
    courseLevelFilter: "All",
    courseStreamFilter: [],
    courseSpecialisationFilter: "All",
    courseDurationFilter: "All",
    specialisationCategoryFilter: "All",
    intake: "All",
  });

  const [limit, setLimit] = useState<number>(12);
  const [courseList, setCourseList] = useState<Courses[]>([]);
  const [total, setTotal] = useState(0);
  const [courseOptions, setCourseOptions] = useState<
    { course_level: string }[]
  >([]);

  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [page, setPage] = useState<number>(
    Number(params.split("page=")[1]) || 1,
  );

  const [registrationLoginPopup, setRegistrationLoginPopup] = useState(false);
  const [emailVerificationPopUp, setEmailVerificationPopup] = useState(false);

  const totalPages = Math.ceil(total / limit);

  // ─── Responsive Limit ────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === "undefined") return;

    let resizeTimeout: NodeJS.Timeout;

    const updateLimit = () => {
      const width = window.innerWidth;
      if (width < 640) setLimit(2);
      else if (width < 1024) setLimit(4);
      else if (width < 1536) setLimit(8);
      else setLimit(12);
    };

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateLimit, 150);
    };

    updateLimit();
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // ─── Fetch Courses ───────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);

    const fetchCourseList = async () => {
      const filter = {
        offset: (page - 1) * limit,
        limit,
        courseLevel: selectedCategory,
        courseStreamFilter: Array.isArray(filteredData.courseStreamFilter)
          ? filteredData.courseStreamFilter.join("|")
          : "",
        durationFilter: filteredData.courseDurationFilter,
        intakeFilter: filteredData.intake,
      };
      try {
        const payload = {
          universityId: String(universityDetail.college_slug),
          filter,
          sort: sortData,
        };

        const response = await getServerCourseList({ payload });

        setCourseOptions([...response.all_course_level]);
        setTotal(response.totalCount);
        setCourseList(response.courses);
      } catch (error) {
        console.error("Error while fetching courses", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    page,
    limit,
    selectedCategory,
    sortData,
    filteredData,
    universityDetail.name,
    universityDetail.id,
  ]);

  // ─── Sync Tab Param ──────────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedCategory(paramsTab ?? "All");
  }, [paramsTab]);

  // ─── Comparison Handlers ─────────────────────────────────────────────────────
  const handleSetCourseComparisonData = useCallback(
    (updater: CourseItem[] | ((prev: CourseItem[]) => CourseItem[])) => {
      const next =
        typeof updater === "function" ? updater(courseComparisonData) : updater;
      dispatch(setCourseComparisonData(next));
    },
    [courseComparisonData, dispatch],
  );

  const comparisonCount = courseComparisonData.length;
  const isComparisonDisabled = comparisonCount < 2;

  // ─── Filter Sidebar Handlers ─────────────────────────────────────────────────
  const openSideBarHandler = useCallback(() => {
    setIsFilterSidebarOpen(true);
    document.body.classList.add("overflow-hidden");
  }, []);

  const sideBarCloseHandler = useCallback(() => {
    setIsFilterSidebarOpen(false);
    document.body.classList.remove("overflow-hidden");
  }, []);
  console.log(courseList, "udschudnucjk")
  console.log(courseOptions, "courseOptions")
  return (
    <div className="university_page_gradient overflow-hidden min-h-screen flex flex-col justify-between">
      <NewNavBar />

      <div>
        <div className="container 2xl:max-w-[1580px] mt-[30px]">
          {/* ── Header & Tab Filters ── */}
          <div className="border-b-2 border-black">
            <Back />
            {universityDetail?.name ? (
              <h1 className="font-semibold text-[18px] lg:text-xl xl:text-2xl 3xl:text-[32px] xl:leading-[37px] mt-4">
                All Courses - {universityDetail?.name},{" "}
                {getFormattedCountryName(
                  universityDetail?.addressInfo?.country ?? "",
                  checkCountry,
                  replacedName,
                )}
              </h1>
            ) : (
              <h1 className="font-semibold text-[18px] lg:text-xl xl:text-2xl 3xl:text-[32px] xl:leading-[37px] mt-4">
                All Courses
              </h1>
            )}
            <div
              className={`mt-5 3xl:mt-7 flex items-center gap-4 md:gap-5 xl:gap-6 flex-wrap ${courseList.length > 0 &&
                "pb-4 md:pb-5 lg:pb-6 xl:pb-7 2xl:pb-8 3xl:pb-9"
                }`}
            >
              {courseList.length > 0 && (
                <button
                  key="All"
                  onClick={() => {
                    setPage(1);
                    setSelectedCategory("All");
                    router.push(
                      `/${universityDetail.college_slug}/all-courses?tab=All`,
                    );
                  }}
                  className={`py-[10px] px-[20px] cursor-pointer rounded-[42px] ff_poppins text-black font-semibold duration-300 ease-in-out text-sm sm:text-base lg:text-lg xl:text-xl ${selectedCategory === "All"
                    ? "bg-[#5E91FF66]"
                    : "bg-[#F3F4F6]"
                    }`}
                >
                  All
                </button>
              )}
              {courseOptions?.map((options) => (
                <button
                  key={options.course_level}
                  onClick={() => {
                    setPage(1);
                    setSelectedCategory(options.course_level);
                    router.push(
                      `/${universityDetail.college_slug}/all-courses?tab=${options.course_level}`,
                    );
                  }}
                  className={`py-[10px] px-[20px] cursor-pointer rounded-[42px] ff_poppins text-black font-semibold duration-300 ease-in-out text-sm sm:text-base lg:text-lg xl:text-xl ${selectedCategory === options.course_level
                    ? "bg-[#5E91FF66]"
                    : "bg-[#F3F4F6]"
                    }`}
                >
                  {options.course_level}
                </button>
              ))}
            </div>
          </div>

          {/* ── Sort / Filter Toolbar ── */}
          <div className="mt-4 sm:mt-12">
            <div className="flex sm:items-center justify-between gap-2 flex-col sm:flex-row">
              <div className="flex sm:items-center justify-between gap-2 xl:gap-16">
                <div className="flex items-center gap-6 cursor-pointer">
                  <Dropdown
                    titleClass="hidden sm:block ff_poppins font-semibold text-xl leading-[30px] text-black"
                    icon={<SortUpArrow />}
                    title={selectedSort ? selectedSort : "Sort"}
                    onSelect={(item) => {
                      if (selectedSort === item.title) {
                        setSortData({ key: "", direction: "" });
                        setSelectedSort("");
                      } else {
                        setSortData({
                          ...sortData,
                          sortingKey: item.key,
                          sortingDirection: item.direction,
                        });
                        setSelectedSort(item.title);
                      }
                    }}
                    content={sortOptionsAllCourse}
                  />

                  <div
                    className="flex items-center gap-1 cursor-pointer"
                    onClick={openSideBarHandler}
                  >
                    <Filter />
                    <p className="ff_poppins font-semibold text-sm sm:text-base md:text-lg xl:text-xl text-black">
                      Filters
                    </p>
                  </div>

                  <FilterSideBar
                    open={isFilterSidebarOpen}
                    setOpen={setIsFilterSidebarOpen}
                    filteredData={filteredData}
                    activeTab={"courses"}
                    from="search"
                    showLevel={false}
                    setFilteredData={setFilteredData}
                  />

                  {isFilterSidebarOpen && (
                    <div
                      className="h-screen w-screen fixed top-0 left-0 bg-[#00000030] z-50"
                      onClick={sideBarCloseHandler}
                    />
                  )}
                </div>
              </div>

              <p className="text-sm sm:text-base md:text-lg xl:text-xl leading-[30px] ff_poppins">
                Showing {courseList?.length} out of {total} results
              </p>
            </div>
          </div>

          {/* ── Course Cards ── */}
          <div className="rounded-[24px] mb-[42px]">
            {loading ? (
              <div className="justify-start py-8 grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <SkeletonCard key={index} />
                ))}
              </div>
            ) : courseList?.length === 0 ? (
              <div>
                <TryAiCourseFindBanner />
                <div className="flex flex-col gap-4 justify-center items-center mt-[30px]">
                  <Image
                    className="w-full max-w-[250px] xl:max-w-[300px]"
                    src="/assets/images/png/notFavorites.png"
                    alt="not favourites"
                    width={369}
                    height={375}
                  />
                  <p className="font-semibold ff_poppins text-2xl text-center">
                    Looks a bit empty here…
                  </p>
                </div>
              </div>
            ) : (
              <div className="justify-start py-8 grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                {courseList.map((item, idx) => {
                  const formattedName = item?.college_name?.replace(
                    /\s+/g,
                    "_",
                  );

                  const avgSalary =
                    item?.roi_info?.salary_range?.min_starting_salary && item?.roi_info?.salary_range?.max_starting_salary
                      ? (Number(item.roi_info.salary_range.min_starting_salary) +
                        Number(item.roi_info.salary_range.max_starting_salary)) /
                      2
                      : 0;

                  const roiYearToPay =
                    avgSalary > 0
                      ? Math.ceil(
                        (Number(item?.fee_details?.annual_tuition_fees) || 0) / avgSalary,
                      )
                      : "";

                  const jobRate =
                    item?.ai_insights?.job_placement_chances ||
                    item?.roi_info?.job_placement_chances ||
                    "";

                  const propData = {
                    id: item.course_id || 0,
                    offerTat: item.offer_tat || "",
                    jobRate: jobRate,
                    roiYearToPay: roiYearToPay,
                    loaDeposit: Number(item.fee_details.loa_deposit) || 0,
                    image: getImageBanner(formattedName, item?.country),
                    course_details: item.course_details,
                    universitiesName: item.college_name || "",
                    popularity: item.popularity || "",
                    category: (item as any).category || "",
                    post_course_work_permit: (item as any).post_course_work_permit,
                    post_course_work_duration: (item as any).post_course_work_duration,
                    currency: item.fee_details.application_currency,
                    applicationFees: item.fee_details.application_fees,
                    tuitionFees:
                      Number(item.fee_details.annual_tuition_fees) || 0,
                    casFee: item.fee_details.loa_deposit || "",
                    duration: item.duration || "",
                    intake_details: item.intake_details || "",
                    campusCity: item.city || "",
                    interview: item.fee_details.interview || "",
                    address_info: item.address_info,
                    state: item.state || "",
                    country: item.country || "",
                    course_id: item.course_id,
                    course_slug: item.course_slug,
                    pre_requisite_details: item.pre_requisite_details,
                    twelfth_percentage: item.twelfth_percentage,
                    englishProficiencyRequirements:
                      item.english_proficiency_requirements,
                    gap_allowed: item.gap_allowed,
                    country_id: Number(item.country_id) ?? 0,
                    tenth_percentage: 0,
                    twelfth_english_percentage: 0,
                    scholarship_details: item.scholarship_details,
                    campus_name: item.campus_name || "",
                    intakeStatus:
                      (item as any)?.intakeStatus ||
                      (item as any)?.intake_status ||
                      "",
                    english_waiver_accepted: item.english_waiver_accepted,
                    englishWaiverAccepted: item.englishWaiverAccepted,
                  };

                  const isLastCard =
                    courseList.length > 1 && idx === courseList.length - 1;

                  return (
                    <div key={idx} className="relative">
                      {isAnonymous && isLastCard && (
                        <div className="absolute top-0 right-0 w-full h-full flex items-center justify-center bg-[#ECDEFF99] backdrop-blur-lg border border-white/20 rounded-xl z-20 flex-col">
                          <div className="max-w-[130px] sm:max-w-[150px] md:max-w-[180px] lg:max-w-[200px] xl:max-w-[227px] w-full">
                            <Image
                              width={227}
                              height={288}
                              src="/assets/images/png/notLogined.png"
                              alt="notLogin"
                            />
                          </div>
                          <p className="ff_poppins font-semibold text-sm sm:text-base lg:text-lg xl:text-xl text-center mb-3">
                            For enhanced and personalised
                          </p>
                          <p className="ff_poppins font-semibold text-sm sm:text-base lg:text-lg xl:text-xl text-center">
                            results, Kindly{" "}
                            <button
                              onClick={() => setRegistrationLoginPopup(true)}
                              className="bg-[#3777FF] text-white underline ff_poppins text-xs sm:text-sm md:text-base font-semibold py-[6px] px-[12px] rounded-[8px] cursor-pointer"
                            >
                              Sign In / Register
                            </button>
                          </p>
                        </div>
                      )}
                      <UniversityCourseCard
                        coursesCards={propData as any}
                        index={1}
                        setComparisonData={handleSetCourseComparisonData as any}
                        comparisonData={courseComparisonData as any}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Pagination ── */}
          {!isAnonymous && totalPages > 1 && (
            <CustomPagination
              totalPages={totalPages}
              onPageChange={(newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          )}

          {comparisonCount > 0 && (
            <>
              {isComparisonDisabled ? (
                <button
                  disabled
                  className="group fixed bottom-5 right-28 sm:right-36 z-40 flex items-center gap-2 rounded-full px-4 py-3 ff_poppins duration-300 ease-in-out bg-white text-[#666] border border-[#D9D9D9] cursor-not-allowed shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]"
                >
                  <GitCompareArrows className="size-5 text-[#666]" />
                  <p className="font-semibold text-sm sm:text-base whitespace-nowrap">
                    {`Select ${2 - comparisonCount} more`}
                  </p>
                  <span className="min-w-[24px] h-6 px-2 rounded-full text-xs font-semibold flex items-center justify-center bg-[#EFEFEF] text-[#555]">
                    {comparisonCount}
                  </span>
                </button>
              ) : (
                <Link
                  href="/ai-course-finder/compare/courses"
                  className="group fixed bottom-5 right-28 sm:right-36 z-40 flex items-center gap-2 rounded-full px-4 py-3 ff_poppins duration-300 ease-in-out bg-button-gradient text-white cursor-pointer hover:scale-[1.06] hover:shadow-[0px_10px_28px_0px_rgba(55,119,255,0.45)]"
                >
                  <GitCompareArrows className="size-5 text-white" />
                  <p className="font-semibold text-sm sm:text-base whitespace-nowrap">
                    View Comparison
                  </p>
                  <span className="min-w-[24px] h-6 px-2 rounded-full text-xs font-semibold flex items-center justify-center bg-white text-[#3777FF]">
                    {comparisonCount}
                  </span>
                </Link>
              )}
            </>
          )}

          {/* ── Auth Popup ── */}
          {registrationLoginPopup && (
            <div className="fixed inset-0 bg-black/70 z-100">
              <div className="w-full h-full fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-60 flex items-center justify-center pb-5 sm:pb-7 md:pb-8 lg:pb-10 3xl:pb-14 pt-3 sm:pt-5 lg:pt-7 3xl:pt-7">
                <div className="bg-white px-5 sm:px-7 md:px-8 lg:px-10 3xl:px-14 py-7 rounded-xl relative mx-3">
                  <span
                    onClick={() => setRegistrationLoginPopup(false)}
                    className="size-4 md:size-5 xl:size-6 absolute top-4 md:top-5 right-4 md:right-5 xl:top-7 xl:right-7 cursor-pointer"
                  >
                    <Close />
                  </span>
                  <CommonAuth setShow={setRegistrationLoginPopup} />
                </div>
              </div>
            </div>
          )}

          {/* ── Email Verification Popup ── */}
          {emailVerificationPopUp && (
            <div
              className="fixed inset-0 bg-black/70 z-100 overflow-hidden flex items-center justify-center"
              onClick={() => setEmailVerificationPopup(false)}
            >
              <EmailVerification
                setEmailVerificationPopup={setEmailVerificationPopup}
              />
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AllCourseHome;
